import { BaseModel, BaseWithId, HasId } from '@app/shared/models/base';
import {
    DrFileMeta,
    DrFileMetaProperties,
    FileUploadStatus
} from '@app/shared/models/dr-file';
import { FileType, FileTypes } from '@app/shared/models/file-type';
import { marker } from '@biesbjerg/ngx-translate-extract-marker';
import * as _ from 'lodash-es';
import { Receiver } from '@app/shared/models/participant';
import { Thumbnailable } from '@app/shared/components/file-upload/thumb-nail-base.component';
import { TranslationEntry } from '@app/shared/models/translatable';
import { BaseWithIdList } from '@app/shared/models/list';
import { ActionListItem } from '@app/shared/components/action-list/action-list.component';
import { environment } from '../../../environments/environment';
import { BackendUriUnits } from '@app/core/services/base/backend-uri-units';
import { SmpUtils } from '@app/core/helpers/utilities';
import { ClaimNetChannelEnum, ClaimNetSubChannel } from '@app/shared/models/recipient-options';
import { CaseFileCategories, CaseFileCategory } from '@app/shared/models/case-file-category';

// TEMPLATES

export class TemplateVersion extends DrFileMeta {
    public description: string;
    public lastmodifiedby: string;
    public selected: boolean;
    public template_category: TranslationEntry;

    public readonly trans = Object.assign(this.trans, {
        description: marker('template_version.description'),
        lastmodifiedby: marker('template_version.last_modified_by'),
        current: marker('template_version.current')
    });

    constructor(json?: any) {
        super(json);
    }

    protected setObjectMappings(json: any): any {
        return Object.assign(super.setObjectMappings(json), {
            template_category: json && json.template_category ? TemplateCategories.getByKey(json.template_category) : null
        });
    }
}

export class Template extends BaseModel implements HasId {
    public display_name: string;
    public hidden: boolean;
    public key: string;
    public sort_order: number;
    public versions: TemplateVersion[];
    public properties: DrFileMetaProperties;
    public not_selectable: boolean;
    public document_type: CaseFileCategories;

    get id() {
        return this.key;
    }

    get nextOrder(): number {
        const highestOrder = _.maxBy(this.versions, (templateVersion: TemplateVersion) => templateVersion.order);
        return highestOrder && highestOrder.order ? (highestOrder.order) + 1 : 1;
    }

    get activeVersion(): TemplateVersion {
        return _.maxBy(this.versions, (fileMeta: TemplateVersion) => fileMeta.version);
    }

    constructor(json?: any) {
        super(json);
    }

    protected setObjectMappings(json: any): any {
        return Object.assign(super.setObjectMappings(json), {
            versions: json && json.versions ? json.versions.map(version => new TemplateVersion(version)) : new Array<TemplateVersion>(),
            properties: json && json.properties ? new DrFileMetaProperties(json.properties) : null
        });
    }

    public removeVersion(templateVersion: TemplateVersion | number): void {
        const version = templateVersion instanceof TemplateVersion ? templateVersion.version : templateVersion;
        this.versions = this.versions.filter(v => v.version !== version);
    }
}

export class CategoryTemplateList extends BaseWithIdList<Template> implements ActionListItem {
    public name: TemplateCategory;
    public template_category: TranslationEntry;
    public document_types: TranslationEntry[];

    get actionListDisplayName() {
        return this.template_category && this.template_category.trans ? this.template_category.trans : this.name;
    }

    get nextOrder(): number {
        const highestOrder = _.maxBy(this.items, (template: Template) => template.sort_order);
        return highestOrder && highestOrder.sort_order ? (highestOrder.sort_order) + 1 : 1;
    }

    constructor(json?: any, name?: TemplateCategory, documentTypes?: TranslationEntry[]) {
        super(Template, json);

        if (name) {
            this.name = name;
            this.template_category = TemplateCategories.getByKey(name);
            this.document_types = documentTypes;
        }
    }

    public switchItems(previousIndex: number, currentIndex: number): void {
        const swappedWith = this.items[currentIndex];
        this.items[currentIndex] = this.items[previousIndex];
        this.items[previousIndex] = swappedWith;

        this.items.forEach(template => template.sort_order = (this.items.indexOf(template) + 1));
    }
}

// DISTRIBUTION JOB

export enum TemplateCategory {
    SMP_ADDITIONAL_DOCUMENT = 'SMP_ADDITIONAL_DOCUMENT',
    SMP_CORRESPONDENCE = 'SMP_CORRESPONDENCE',
    SMP_MAINDOCUMENT = 'SMP_MAINDOCUMENT',
    OTHER = 'OTHER',
    PHOTO = 'PHOTO',
    SMP_PLACEHOLDER = 'SMP_PLACEHOLDER'
}

export class TemplateCategories {

    public static readonly ALL: TranslationEntry[] = [
        { key: TemplateCategory.SMP_ADDITIONAL_DOCUMENT, trans: marker(`template_categories.SMP_ADDITIONAL_DOCUMENT`) },
        { key: TemplateCategory.SMP_CORRESPONDENCE, trans: marker(`template_categories.SMP_CORRESPONDENCE`) },
        { key: TemplateCategory.SMP_MAINDOCUMENT, trans: marker(`template_categories.SMP_MAINDOCUMENT`) },
        { key: TemplateCategory.SMP_PLACEHOLDER, trans: marker(`template_categories.SMP_PLACEHOLDER`) }
    ];

    public static getByKey(key: string): TranslationEntry {
        return _.find(TemplateCategories.ALL, (templateCategory: TranslationEntry) => templateCategory.key === key);
    }

    public static get SMP_ADDITIONAL_DOCUMENT() {
        return TemplateCategories.getByKey(TemplateCategory.SMP_ADDITIONAL_DOCUMENT);
    }

    public static get SMP_CORRESPONDENCE() {
        return TemplateCategories.getByKey(TemplateCategory.SMP_CORRESPONDENCE);
    }

    public static get SMP_MAINDOCUMENT() {
        return TemplateCategories.getByKey(TemplateCategory.SMP_MAINDOCUMENT);
    }

    public static get SMP_PLACEHOLDER() {
        return TemplateCategories.getByKey(TemplateCategory.SMP_PLACEHOLDER);
    }
}

export enum DistributionDocumentState {
    INITIAL = 'INITIAL',
    READY = 'READY',
    ERROR = 'ERROR'
}

export enum DistributionJobState {
    INITIAL = 'INITIAL',
    PREPARING_SCHEDULED = 'PREPARING_SCHEDULED',
    ERROR_PREPARING = 'ERROR_PREPARING',
    PREPARED = 'PREPARED',
    SENDING_SCHEDULED = 'SENDING_SCHEDULED',
    ERROR_SENDING = 'ERROR_SENDING',
    SENT = 'SENT'
}

export enum DistributionJobChannel {
    CONNECT = 'CONNECT',
    PRINT = 'PRINT',
    SIGNING = 'SIGNING',
    CLAIMNET = 'CLAIMNET',
    EMAIL = 'EMAIL'
}

export enum InsignState {
    NONE = 'NONE',
    SIGNING = 'SIGNING',
    FINISHED = 'FINISHED'
}

export class DistributionJobStates {

    public static readonly ALL: TranslationEntry[] = [
        { key: DistributionJobState.INITIAL, trans: marker('distribution_job_state.INITIAL') },
        { key: DistributionJobState.PREPARING_SCHEDULED, trans: marker('distribution_job_state.PREPARING_SCHEDULED') },
        { key: DistributionJobState.ERROR_PREPARING, trans: marker('distribution_job_state.ERROR_PREPARING') },
        { key: DistributionJobState.PREPARED, trans: marker('distribution_job_state.PREPARED') },
        { key: DistributionJobState.SENDING_SCHEDULED, trans: marker('distribution_job_state.SENDING_SCHEDULED') },
        { key: DistributionJobState.ERROR_SENDING, trans: marker('distribution_job_state.ERROR_SENDING') },
        { key: DistributionJobState.SENT, trans: marker('distribution_job_state.SENT') }
    ];

    public static getByKey(key: string): TranslationEntry {
        return _.find(DistributionJobStates.ALL, (state: TranslationEntry) => state.key === key);
    }

    public static get INITIAL() {
        return DistributionJobStates.getByKey(DistributionJobState.INITIAL);
    }

    public static get PREPARING_SCHEDULED() {
        return DistributionJobStates.getByKey(DistributionJobState.PREPARING_SCHEDULED);
    }

    public static get ERROR_PREPARING() {
        return DistributionJobStates.getByKey(DistributionJobState.ERROR_PREPARING);
    }

    public static get PREPARED() {
        return DistributionJobStates.getByKey(DistributionJobState.PREPARED);
    }

    public static get SENDING_SCHEDULED() {
        return DistributionJobStates.getByKey(DistributionJobState.SENDING_SCHEDULED);
    }

    public static get ERROR_SENDING() {
        return DistributionJobStates.getByKey(DistributionJobState.ERROR_SENDING);
    }

    public static get SENT() {
        return DistributionJobStates.getByKey(DistributionJobState.SENT);
    }
}

export class DistributionJobChannels {

    public static readonly ALL: TranslationEntry[] = [
        { key: DistributionJobChannel.CONNECT, trans: marker('distribution_channel.connect') },
        { key: DistributionJobChannel.PRINT, trans: marker('distribution_channel.print') },
        { key: DistributionJobChannel.SIGNING, trans: marker('distribution_channel.in_sign') },
        { key: DistributionJobChannel.CLAIMNET, trans: marker('distribution_channel.claim_net') },
        { key: DistributionJobChannel.EMAIL, trans: marker('distribution_channel.email') }
    ];

    public static getByKey(key: string): TranslationEntry {
        return _.find(DistributionJobChannels.ALL, (channel: TranslationEntry) => channel.key === key);
    }

    public static get CONNECT() {
        return DistributionJobChannels.getByKey(DistributionJobChannel.CONNECT);
    }

    public static get PRINT() {
        return DistributionJobChannels.getByKey(DistributionJobChannel.PRINT);
    }

    public static get SIGNING() {
        return DistributionJobChannels.getByKey(DistributionJobChannel.SIGNING);
    }

    public static get CLAIM_NET() {
        return DistributionJobChannels.getByKey(DistributionJobChannel.CLAIMNET);
    }

    public static get EMAIL() {
        return DistributionJobChannels.getByKey(DistributionJobChannel.EMAIL);
    }
}

export class DistributionDocument extends BaseWithId implements Thumbnailable {
    public client_id: string;
    public job_id: string;
    public item_id: string;
    public copy: boolean;
    public created: Date;
    public display_name: string;
    public document_date: Date;
    public file_extension: string;
    public document_key:  string; // original from before prep
    public merge: boolean;
    public print_date: Date;
    public selected: boolean;
    public sort_order: number;
    public state: DistributionDocumentState;
    public fileUploadStatus: FileUploadStatus;
    public state_description: string;
    public document_type: CaseFileCategories;
    public is_template: boolean;
    public is_placeholder: boolean;
    public properties: DrFileMetaProperties;
    public workflow_id: string;

    public link: string; // after prep this is calculated by the backend
    public fileType: FileType;

    public readonly trans = {
        copy: marker('distribution_document.copy'),
        document_date: marker('distribution_document.document_date'),
        placeholder: marker('detailed_processing.distribution.label.placeholder'),
        category: ''
    };

    get reorderPath() {
        return `${this.id}/metadata/order`;
    }

    get thumbnailLink(): string {
        return this.state === DistributionDocumentState.READY ?
               this.link :
              (this.is_template ? `${environment.apiUrl}/${BackendUriUnits.API}/${BackendUriUnits.DISTRIBUTION}/${BackendUriUnits.JOBS}/${this.job_id}/items/${this.item_id}/documents/${this.id}/preview` :
               this.document_key);
    }

    get thumbnailFileName(): string {
        return this.display_name;
    }

    get thumbnailStatus(): FileUploadStatus {
        return this.fileUploadStatus;
    }

    constructor(json?: any) {
        super(json);

        if (this.document_type) {
            this.trans.category = CaseFileCategory[this.document_type].trans;
        }
    }

    protected setObjectMappings(json: any): any {
        const ext = json ? (json.file_extension ? json.file_extension.toLowerCase() :
                           (json.display_name ? SmpUtils.getExtensionFromFileName(json.display_name) : null)) :
                            null;
        return Object.assign(super.setObjectMappings(json), {
            created: json && json.created ? new Date(json.created) : null,
            document_date: json && json.document_date ? new Date(json.document_date) : null,
            print_date: json && json.print_date ? new Date(json.print_date) : null,
            fileType: ext ? FileTypes.getByKey(ext) : (json && json.is_template ? FileTypes.XSLT : null),
            properties: json && json.properties ? new DrFileMetaProperties(json.properties) : null
        });
    }

}

export class DistributionPlaceholder extends DistributionDocument {
    public placeholder_links: DistributionDocument[]; //{ link: string, name: string, properties: string }[];

    constructor(json?: any) {
        super(json);
    }

    protected setObjectMappings(json: any): any {
        return Object.assign(super.setObjectMappings(json), {
            placeholder_links: json && json.placeholder_links ? json.placeholder_links.map(placeholderLink => {
                if (!(placeholderLink instanceof DistributionDocument)) {
                    const props = JSON.parse(placeholderLink.properties);
                    placeholderLink.display_name = placeholderLink.name;
                    delete placeholderLink.name;
                    placeholderLink.properties = props;
                    placeholderLink.document_type = props.smp_category;
                    placeholderLink.state = DistributionDocumentState.INITIAL;
                    placeholderLink.document_key = placeholderLink.link;
                    placeholderLink = new DistributionDocument(placeholderLink);
                }

                return placeholderLink;
            }) : []
        });
    }
}

export enum DistributionJobItemType {
    SMP_CASE = 'SMP_CASE',
    CASE = 'CASE',
    INVOICE = 'INVOICE'
}

export class DistributionJobItem extends BaseWithId {
    public client_id: string;
    public job_id: string;
    public type: DistributionJobItemType;
    public order: number;
    public documents: DistributionDocument[];

    constructor(json?: any) {
        super(json);
    }

    protected setObjectMappings(json: any): any {
        return Object.assign(super.setObjectMappings(json), {
            documents: json && json.documents ? json.documents.map(doc => doc.is_placeholder ? new DistributionPlaceholder(doc) : new DistributionDocument(doc)) : new Array<DistributionDocument>()
        });
    }
}

export class DistributionJob extends BaseWithId {
    public client_id: string;
    public channel: DistributionJobChannel;
    public channel_value: string;
    public distribution_date: Date;
    public distribution_option: string;
    public distribution_option_value: string;
    public exclude_calculation: boolean;
    public finished_by: string;
    public max_filesize: number;
    public merge_lock: string;
    public merge_pdf: boolean;
    public merge_pdf_filename: string;
    public print_copy: boolean;
    public state: DistributionJobState;
    public state_text: string;
    public recipient_id: string;
    public recipient_denomination: string;
    public receiver: Receiver;
    public workflow_id: string;

    public envelope_id: string;
    public envelope_type: DistributionJobItemType;

    // Workflow does not items but will have documents so we also need the collection at this level ...
    public documents: DistributionDocument[];

    // Technically a distribution job can hold multiple items (ask Stefan for more info).
    // For the time being for SMP this will always be only one item. Therefor we create below prop

    private items: DistributionJobItem[];

    get item() {
        return this.items && this.items.length > 0 ? this.items[0] : null;
    }

    get selectedTemplateKeys(): HasId[] {
        const selectedDocuments = this.documentsArray || [];

        return selectedDocuments.map(doc => {
            return {
                id: doc.document_key
            };
        });
    }

    get documentsArray() {
        return this.workflow_id ? this.documents : (this.item && this.item.documents);
    }

    get isInSignSignNow(): boolean {
        return isInSignDistributionJob(this) && this.insign_send_now;
    }

    public readonly trans = {
        channel: marker('distribution_job.channel'),
        merge_pdf: marker('distribution_job.merge_pdf'),
        receiver: marker('distribution_job.receiver'),
    };

    set item(item: DistributionJobItem) {
        this.items = new Array<DistributionJobItem>();

        if (item) {
            this.envelope_id = item.id;
            this.envelope_type = item.type;
            this.items.push(item);
        }
    }

    get nextOrder(): number {
        const selectedDocuments = this.documentsArray || [];
        const highestOrderMeta = _.maxBy(selectedDocuments, (doc: DistributionDocument) => doc.sort_order);
        return highestOrderMeta && highestOrderMeta.sort_order ? (highestOrderMeta.sort_order) + 1 : 1;
    }

    constructor(json?: any) {
        super(json);
    }

    public static createSubTypeByChannel(json?: any): DistributionJob {
        switch (json.channel) {
            case DistributionJobChannel.PRINT: {
                return new PrintDistributionJob(json);
            }
            case DistributionJobChannel.EMAIL: {
                return new EmailDistributionJob(json);
            }
            case DistributionJobChannel.SIGNING: {
                return new InsignDistributionJob(json);
            }
            case DistributionJobChannel.CLAIMNET: {
                return new ClaimsNetDistributionJob(json);
            }
            default: {
                return new DistributionJob(json);
            }
        }
    }

    protected setObjectMappings(json: any): any {
        return Object.assign(super.setObjectMappings(json), {
            items: json && !json.workflow_id ? (json.items ? json.items.map(item => new DistributionJobItem(item)) : new Array<DistributionJobItem>()) : undefined,
            receiver: json && json.receiver ? new Receiver(json.receiver) : null,
            documents: json && json.workflow_id ? (json.documents ? json.documents.map(doc => doc.is_placeholder ? new DistributionPlaceholder(doc): new DistributionDocument(doc)) : []) : undefined
        });
    }

    public sortDocumentsArray(): void {
        if (this.workflow_id) {
            this.documents = this.documents.sort(doc => doc.sort_order);
        } else if (this.item) {
            this.item.documents = this.item.documents.sort(doc => doc.sort_order);
        }
    }

    public addDocument(doc: DistributionDocument): void {
        // Since there is no way to link DistributionJobItemDocuments to the original documents, we can only check the sort_order at this time to see which one needs to be updated in the list
        const toReplace = this.documentsArray.find(existingDoc => existingDoc.id === doc.id || (!existingDoc.id && doc.id && doc.sort_order === existingDoc.sort_order));

        if (toReplace) {
            this.updateDocument(doc);
        } else {
            this.documentsArray.push(doc);
        }

        this.sortDocumentsArray();
    }

    public updateDocument(updatedDoc: DistributionDocument): void {
        // Since there is no way to link DistributionJobItemDocuments to the original documents, we can only check the sort_order at this time to see which one needs to be updated in the list
        const toReplace = this.documentsArray.find(existingDoc => existingDoc.id === updatedDoc.id || (!existingDoc.id && updatedDoc.id && existingDoc.sort_order === updatedDoc.sort_order));
        const idx = this.documentsArray.indexOf(toReplace);
        this.documentsArray[idx] = updatedDoc;
    }

    public getIcon(): string {
        switch (this.state) {
            case DistributionJobState.INITIAL || DistributionJobState.PREPARING_SCHEDULED || DistributionJobState.ERROR_PREPARING: {
                return `fa-envelope-open`;
            }
            case DistributionJobState.PREPARED || DistributionJobState.SENDING_SCHEDULED || DistributionJobState.ERROR_SENDING: {
                return `fa-envelope-open-text`;
            }
            case DistributionJobState.SENT: {
                return this.getSentIcon();
            }
            default: {
                return '';
            }
        }
    }

    protected getSentIcon(): string {
        return 'fa-envelope';
    }

    public getTooltip(): string {
        if (this.state !== DistributionJobState.SENT) {
            return DistributionJobStates.getByKey(this.state) && DistributionJobStates.getByKey(this.state).trans;
        } else {
            try {
                return this.getSentTooltip();
            } catch (err) {

            }
        }
    }

    protected getSentTooltip(): string {
        return this.isInSignSignNow ? marker('detailed_processing.distribution.label.ready_for_signing') : DistributionJobStates.SENT.trans;
    }

}

export class PrintDistributionJob extends DistributionJob {

    constructor(json?: any) {
        super(json);
    }

    protected getSentIcon(): string {
        return 'fa-print';
    }

    protected getSentTooltip(): string {
        return marker('distribution_job_state.PRINT');
    }
}

export class EmailDistributionJob extends DistributionJob {
    public email_to: string;
    public email_cc: string;
    public email_bcc: string;

    public email_subject: string;
    public email_body: string;

    constructor(json?: any) {
        super(json);
    }

    public readonly trans = Object.assign(this.trans, {
        email_to: marker('distribution_job.email_to'),
        email_cc: marker('distribution_job.email_cc'),
        email_bcc: marker('distribution_job.email_bcc'),
        email_subject: marker('distribution_job.email_subject'),
        email_body: marker('distribution_job.email_body')
    });
}

export class InsignDistributionJob extends DistributionJob {
    public insign_send_email: boolean;
    public insign_send_sms: boolean;
    public insign_send_now: boolean;

    public insign_email_to: string;
    public insign_email_subject: string;
    public insign_email_body_before_link: string;
    public insign_email_body_after_link: string;
    public insign_email_textblock_before_link_key: string;
    public insign_email_teextblock_after_link_key: string;

    public insign_sms_to: string;
    public insign_sms_body: string;

    public insign_session_valid_for_hours: number;
    public insign_session_valid_until: Date;
    public insign_state: InsignState;

    public insign_link: string;

    constructor(json?: any) {
        super(json);
    }

    public readonly trans = Object.assign(this.trans, {
        insign_send_email: marker('distribution_job.insign_send_email'),
        insign_email_to: marker('distribution_job.insign_email_to'),
        insign_email_subject: marker('distribution_job.insign_email_subject'),
        insign_email_body_before_link: marker('distribution_job.insign_email_body_before_link'),
        insign_email_body_after_link: marker('distribution_job.insign_email_body_after_link'),
        insign_send_sms: marker('distribution_job.insign_send_sms'),
        insign_sms_to: marker('distribution_job.insign_sms_to'),
        insign_sms_body: marker('distribution_job.insign_sms_body'),
        insign_session_valid_for_hours: marker('distribution_job.insign_session_valid_for_hours'),
        insign_state: marker('distribution_job.insign_state'),
        insign_send_now: marker('distribution_job.insign_send_now')
    });

    protected setObjectMappings(json: any): any {
        return Object.assign(super.setObjectMappings(json), {
            insign_session_valid_until: json && json.insign_session_valid_until ? new Date(json.insign_session_valid_until) : null
        });
    }

    protected getSentIcon(): string {
        return this.insign_state === InsignState.FINISHED ? 'fa-pen-alt' : super.getSentIcon();
    }

    protected getSentTooltip(): string {
        return this.insign_state === InsignState.FINISHED ? marker('distribution_job_state.SIGNED') : super.getSentTooltip();
    }
}

export class ClaimsNetDistributionJob extends DistributionJob {
    public distribution_option: ClaimNetChannelEnum;
    public distribution_option_value: string;
    public sub_channel: ClaimNetSubChannel;
    public sub_channel_value: string;
    public print_remove_pictures: boolean;
    public recipient_name: string;

    constructor(json?: any) {
        super(json);
    }

    public readonly trans = Object.assign(this.trans, {
        recipient: marker('distribution_job.recipient'),
        print_remove_pictures: marker('distribution_job.print_remove_pictures'),
    });
}

export function isPrintDistributionJob(job: DistributionJob): job is PrintDistributionJob {
    return job.channel === DistributionJobChannel.PRINT;
}

export function isEmailDistributionJob(job: DistributionJob): job is EmailDistributionJob {
    return job.channel === DistributionJobChannel.EMAIL;
}

export function isClaimsNetDistributionJob(job: DistributionJob): job is ClaimsNetDistributionJob {
    return job.channel === DistributionJobChannel.CLAIMNET;
}

export function isInSignDistributionJob(job: DistributionJob): job is InsignDistributionJob {
    return job.channel === DistributionJobChannel.SIGNING;
}
